/**
 * Shared constants and utilities for chat side panel drag-resize (desktop only).
 * Used by Student Chat Widget and Capio Copilot.
 */

export const PANEL_WIDTH_DEFAULT = 520;
export const PANEL_WIDTH_MIN = 398;
export const PANEL_WIDTH_MAX = 840;
export const PANEL_VIEWPORT_MARGIN = 48;

export const PANEL_COMPACT_HEIGHT_MIN = 320;
/** Default compact panel height as a fraction of available compact viewport height. */
export const PANEL_COMPACT_HEIGHT_RATIO = 0.75;
/** Horizontal gap from viewport right to compact panel (matches default launcher mx). */
export const PANEL_COMPACT_HORIZONTAL_PADDING = 24;
/** Inner compact panel width so footprint matches full-height default (496 + 24 = 520). */
export const PANEL_COMPACT_WIDTH_DEFAULT =
  PANEL_WIDTH_DEFAULT - PANEL_COMPACT_HORIZONTAL_PADDING;
/** Rounded corners on compact floating panel (matches settings preview). */
export const PANEL_COMPACT_BORDER_RADIUS = 12;
/** Minimum gap from viewport bottom when compact panel is floating (matches settings preview). */
export const PANEL_COMPACT_VIEWPORT_INSET = 16;
export const PANEL_VIEWPORT_VERTICAL_MARGIN = 16;

export const CHAT_WIDGET_PANEL_WIDTH_KEY = 'capio-engage-chat-panel-width';
export const CHAT_WIDGET_PANEL_COMPACT_WIDTH_KEY = 'capio-engage-chat-panel-compact-width';
export const COPILOT_PANEL_WIDTH_KEY = 'capio-engage-copilot-panel-width';

/**
 * Effective max width is the smaller of MAX or viewport (minus margin).
 * Call from client so window is available.
 */
export function getMaxPanelWidth(): number {
  if (typeof window === 'undefined') return PANEL_WIDTH_MAX;
  return Math.min(PANEL_WIDTH_MAX, window.innerWidth - PANEL_VIEWPORT_MARGIN);
}

/**
 * Clamp width to [MIN, effectiveMax] and ensure integer.
 */
/**
 * Compact floating panel inner width: stored footprint minus horizontal padding
 * (typically launcher mx) so total span from viewport right matches full-height width.
 */
export function getCompactPanelDisplayWidth(
  footprintWidth: number,
  horizontalPadding: number,
  effectiveMax: number = getMaxPanelWidth(),
): number {
  const safePadding = Math.max(0, horizontalPadding);
  const displayMax = Math.max(0, effectiveMax - safePadding);
  const displayMin = Math.min(Math.max(0, PANEL_WIDTH_MIN - safePadding), displayMax);
  const raw = Math.round(footprintWidth - safePadding);
  return Math.round(Math.max(displayMin, Math.min(displayMax, raw)));
}

/** Persisted footprint width from compact inner width + horizontal padding. */
export function getFootprintWidthFromCompactDisplay(
  displayWidth: number,
  horizontalPadding: number,
  effectiveMax: number = getMaxPanelWidth(),
): number {
  const safePadding = Math.max(0, horizontalPadding);
  return clampPanelWidth(displayWidth + safePadding, effectiveMax);
}

export function clampPanelWidth(
  width: number,
  effectiveMax: number = getMaxPanelWidth()
): number {
  // Allow narrower widths when viewport max is below the default minimum.
  const safeMax = Math.max(0, Math.round(effectiveMax));
  const safeMin = Math.min(PANEL_WIDTH_MIN, safeMax);
  const clamped = Math.round(Math.max(safeMin, Math.min(safeMax, width)));
  return clamped;
}

/**
 * Read persisted panel width from localStorage. Returns null if missing or invalid.
 */
export function readStoredPanelWidth(storageKey: string): number | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw === null) return null;
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 0) return null;
    return Math.min(n, PANEL_WIDTH_MAX);
  } catch {
    return null;
  }
}

/**
 * Persist panel width to localStorage. No-op if not in browser.
 */
export function writeStoredPanelWidth(
  storageKey: string,
  width: number
): void {
  if (typeof window === 'undefined') return;
  try {
    const value = clampPanelWidth(width);
    localStorage.setItem(storageKey, String(value));
  } catch {
    // Ignore storage errors (e.g. private mode)
  }
}

export type ViewportHeightBucket = '<700' | '700–899' | '900–1099' | '1100+';

export function getViewportHeightBucket(height: number): ViewportHeightBucket {
  if (height < 700) return '<700';
  if (height < 900) return '700–899';
  if (height < 1100) return '900–1099';
  return '1100+';
}

export function getMaxCompactPanelHeight(
  viewportHeight: number,
  verticalMargin: number = PANEL_VIEWPORT_VERTICAL_MARGIN,
): number {
  return Math.max(0, viewportHeight - verticalMargin);
}

export function clampPanelCompactHeight(
  height: number,
  maxHeight: number,
): number {
  const safeMax = Math.max(PANEL_COMPACT_HEIGHT_MIN, Math.round(maxHeight));
  return Math.round(Math.max(PANEL_COMPACT_HEIGHT_MIN, Math.min(safeMax, height)));
}

/** Default compact height: 75% of available compact viewport, clamped to [min, max]. */
export function getDefaultCompactPanelHeight(compactViewportHeight: number): number {
  const preferred = Math.round(compactViewportHeight * PANEL_COMPACT_HEIGHT_RATIO);
  const maxHeight = getMaxCompactPanelHeight(compactViewportHeight, 0);
  return clampPanelCompactHeight(preferred, maxHeight);
}

export function resolveCompactPanelHeight(options: {
  preferredHeight: number;
  viewportHeight: number;
  minHeight?: number;
  verticalMargin?: number;
}): { height: number; forceFullHeight: boolean } {
  const {
    preferredHeight,
    viewportHeight,
    minHeight = PANEL_COMPACT_HEIGHT_MIN,
    verticalMargin = PANEL_VIEWPORT_VERTICAL_MARGIN,
  } = options;
  const available = viewportHeight - verticalMargin;
  if (available < minHeight) {
    return { height: Math.max(0, available), forceFullHeight: true };
  }
  return {
    height: Math.min(preferredHeight, available),
    forceFullHeight: false,
  };
}
